import { useEffect, useMemo, useState } from 'react';
import { getAllResults } from '../bench.js';
import { findModel } from '../catalog.js';
import {
  decodeReceiptSlug,
  exportReceiptAs,
  receiptIdForResult,
  receiptShareSlug,
  signBenchmarkReceipt,
  verifyReceipt,
} from '../receipt.js';
import type { BenchmarkResult, MachineProfile } from '../types.js';

interface ReceiptsTabProps {
  machine: MachineProfile;
  mode: 'beginner' | 'pro';
  initialReceiptSlug?: string;
  onChangeReceiptSlug: (slug: string | undefined) => void;
}

export function ReceiptsTab({ machine, mode, initialReceiptSlug, onChangeReceiptSlug }: ReceiptsTabProps) {
  const history = useMemo(() => getAllResults().filter((r) => r.machineId === machine.id), [machine.id]);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(history[0]?.id ?? null);

  const sharedReceipt = useMemo(
    () => (initialReceiptSlug ? decodeReceiptSlug(initialReceiptSlug) : null),
    [initialReceiptSlug],
  );

  useEffect(() => {
    if (!initialReceiptSlug) return;
    const match = history.find((r) => receiptIdForResult(r) === sharedReceipt?.receiptId);
    if (match && match.id !== selectedResultId) setSelectedResultId(match.id);
  }, [history, initialReceiptSlug, selectedResultId, sharedReceipt]);

  useEffect(() => {
    if (initialReceiptSlug) return;
    if (history.length === 0) {
      if (selectedResultId !== null) setSelectedResultId(null);
      return;
    }
    if (!selectedResultId || !history.some((r) => r.id === selectedResultId)) {
      setSelectedResultId(history[0].id);
    }
  }, [history, initialReceiptSlug, selectedResultId]);

  const focused = useMemo(
    () => history.find((r) => r.id === selectedResultId) ?? null,
    [history, selectedResultId],
  );
  const localReceipt = useMemo(
    () => (focused ? signBenchmarkReceipt(focused, machine) : null),
    [focused, machine],
  );
  const receipt = localReceipt ?? sharedReceipt;
  const activeResult = focused ?? receipt?.payload.result ?? null;
  const verified = receipt ? verifyReceipt(receipt) : false;

  function focusResult(result: BenchmarkResult): void {
    setSelectedResultId(result.id);
    onChangeReceiptSlug(receiptShareSlug(signBenchmarkReceipt(result, machine)));
  }

  return (
    <div className="lab-receipts">
      <aside className="lab-receipts-list">
        <h4>Bench runs on {machine.nickname}</h4>
        {history.length === 0 ? (
          <p className="lab-empty">Run a benchmark to see receipts here.</p>
        ) : (
          <ul>
            {history.map((r) => {
              const m = findModel(r.modelId);
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`lab-receipt-item ${focused?.id === r.id ? 'lab-receipt-active' : ''}`}
                    onClick={() => focusResult(r)}
                  >
                    <strong>{m?.displayName ?? r.modelId}</strong>
                    <span>
                      {r.taskCategory} · {r.tokensPerSecond} tok/s
                    </span>
                    <time>{new Date(r.timestamp).toLocaleString()}</time>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      <section className="lab-receipt-detail">
        {!activeResult && <p className="lab-empty">Select a run to view its receipt.</p>}
        {activeResult && receipt && (
          <>
            <header>
              <div>
                <span className="section-kicker">Signed receipt</span>
                <h3>{findModel(activeResult.modelId)?.displayName ?? activeResult.modelId}</h3>
                <p>
                  {activeResult.taskCategory} · {activeResult.quantization} · {activeResult.runtime} ·{' '}
                  {new Date(activeResult.timestamp).toLocaleString()}
                </p>
              </div>
              <div className="lab-receipt-actions">
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => copyText(exportReceiptAs(receipt, 'json'))}
                >
                  Copy JSON
                </button>
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => copyText(exportReceiptAs(receipt, 'csv'))}
                >
                  Copy CSV
                </button>
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => copyText(exportReceiptAs(receipt, 'markdown'))}
                >
                  Copy Markdown
                </button>
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => copyText(exportReceiptAs(receipt, 'slsa'))}
                >
                  Copy SLSA
                </button>
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => {
                    onChangeReceiptSlug(receiptShareSlug(receipt));
                    copyText(receipt.shareUrl);
                  }}
                >
                  Copy share URL
                </button>
              </div>
            </header>

            <div className="lab-receipt-grid">
              <article>
                <span>tokens/sec</span>
                <strong>{activeResult.tokensPerSecond}</strong>
              </article>
              <article>
                <span>quality</span>
                <strong>{activeResult.qualityScore.toFixed(1)}</strong>
              </article>
              <article>
                <span>pass rate</span>
                <strong>{(activeResult.passRate * 100).toFixed(0)}%</strong>
              </article>
              <article>
                <span>stability</span>
                <strong>{(activeResult.stabilityScore * 100).toFixed(0)}%</strong>
              </article>
              <article>
                <span>tool-use</span>
                <strong>{(activeResult.toolUseSuccess * 100).toFixed(0)}%</strong>
              </article>
              <article>
                <span>diff quality</span>
                <strong>{activeResult.diffQuality.toFixed(1)}</strong>
              </article>
              <article>
                <span>repair success</span>
                <strong>{(activeResult.repairSuccess * 100).toFixed(0)}%</strong>
              </article>
              <article>
                <span>agent completion</span>
                <strong>{activeResult.agentCompletionScore.toFixed(0)}</strong>
              </article>
              <article>
                <span>bench confidence</span>
                <strong>{(activeResult.benchConfidence * 100).toFixed(0)}%</strong>
              </article>
              <article>
                <span>memory used</span>
                <strong>{activeResult.memoryUseGb} GB</strong>
              </article>
              <article>
                <span>VRAM used</span>
                <strong>{activeResult.vramUseGb} GB</strong>
              </article>
            </div>

            <div className="lab-receipt-proof">
              <h4>Proof</h4>
              <dl>
                <div>
                  <dt>Receipt ID</dt>
                  <dd>
                    <code>{receipt.receiptId}</code>
                  </dd>
                </div>
                <div>
                  <dt>Content CID</dt>
                  <dd>
                    <code>{receipt.contentId}</code>
                  </dd>
                </div>
                <div>
                  <dt>Ed25519 signature</dt>
                  <dd>
                    <code>
                      {receipt.signature.slice(0, 32)}…{receipt.signature.slice(-16)}
                    </code>
                  </dd>
                </div>
                <div>
                  <dt>Public key</dt>
                  <dd>
                    <code>{receipt.publicKeyHex}</code>
                  </dd>
                </div>
                <div>
                  <dt>Share URL</dt>
                  <dd>
                    <code>{receipt.shareUrl}</code>
                  </dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{activeResult.source}</dd>
                </div>
                <div>
                  <dt>Verified locally</dt>
                  <dd>
                    <code style={{ color: verified ? 'var(--mint)' : 'var(--coral)' }}>
                      {verified ? 'yes · signature matches' : 'no'}
                    </code>
                  </dd>
                </div>
              </dl>
            </div>

            <details className="lab-receipt-replay">
              <summary>Replay instructions</summary>
              <dl>
                <div>
                  <dt>API endpoint</dt>
                  <dd>
                    <code>{receipt.payload.replay.apiEndpoint}</code>
                  </dd>
                </div>
                <div>
                  <dt>Prompt</dt>
                  <dd>
                    <code>{receipt.payload.replay.prompt}</code>
                  </dd>
                </div>
                <div>
                  <dt>How to reproduce</dt>
                  <dd>{receipt.payload.replay.instructions}</dd>
                </div>
              </dl>
            </details>

            {mode === 'pro' && (
              <details className="lab-receipt-json" open>
                <summary>Raw JSON</summary>
                <pre>{exportReceiptAs(receipt, 'json')}</pre>
              </details>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function copyText(text: string): void {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return;
  navigator.clipboard.writeText(text).catch(() => {});
}
